import React, { useState, useEffect } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { pmt2Service } from '@/services/pmt2Service';
import { GpoPerformanceRow } from '@/services/pmt2GpoPerformanceService';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { format } from 'date-fns';

interface GpoPerformanceDashboardProps {
  product: PMT2Product | null;
}

// Define the structure for Next GPO Possibilities row
interface NextGpoPossibility {
  plusMinus: number;
  plusMinusValue: number;
  newGpo: number | string;
  newMaxBid: number | string;
  asin: string;
  date: Date;
  yourName: string;
  dataElement: string;
  newValue: number | string;
}

// Define the multipliers for the +/- column
const MULTIPLIERS = [5, 4, 3, 2, 1, -1, -2, -3, -4, -5];

// Define the data element text
const DATA_ELEMENT = "Gross Profit Override";

/**
 * GPO Performance Dashboard Component
 * 
 * Displays a table of GPO performance data for a selected product
 */
const GpoPerformanceDashboard: React.FC<GpoPerformanceDashboardProps> = ({ product }) => {
  const [data, setData] = useState<GpoPerformanceRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [increment, setIncrement] = useState<string>("0.25");
  const [nextGpoPossibilities, setNextGpoPossibilities] = useState<NextGpoPossibility[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!product) {
        setData([]);
        setError(null);
        return setLoading(false);
      }

      try {
        setLoading(true);
        const performanceData = await pmt2Service.getGpoPerformanceData(product.companyName, product.id);
        setData(performanceData);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err instanceof Error ? err.message : err);
        setError('Failed to load GPO performance data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [product]);

  // Calculate Next GPO Possibilities whenever data or increment changes
  useEffect(() => {
    if (data.length > 0 && product) {
      calculateNextGpoPossibilities();
    }
  }, [data, increment, product]);

  // Calculate the Next GPO Possibilities
  const calculateNextGpoPossibilities = () => {
    if (!product || data.length === 0) return;

    // Get the Gross Profit from the first row
    const grossProfit = data[0]?.grossProfit || 0;
    
    // Parse the increment value
    const incrementValue = parseFloat(increment) || 0;
    
    // Get the ASIN Manager from the product data
    const asinManager = product['ASIN Manager'] || '';
    
    // Get the current ASIN
    const asin = product.id || '';
    
    // Get today's date
    const today = new Date();
    
    // Calculate the possibilities
    const possibilities = MULTIPLIERS.map(multiplier => {
      const plusMinusValue = multiplier * incrementValue;
      const newGpoValue = grossProfit + plusMinusValue;
      
      return {
        plusMinus: multiplier,
        plusMinusValue,
        newGpo: newGpoValue <= 0 ? "-" : newGpoValue,
        newMaxBid: newGpoValue <= 0 ? "-" : newGpoValue * 0.2,
        asin,
        date: today,
        yourName: asinManager,
        dataElement: DATA_ELEMENT,
        newValue: newGpoValue <= 0 ? "-" : newGpoValue
      };
    });
    
    setNextGpoPossibilities(possibilities);
  };

  // Handle increment change
  const handleIncrementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIncrement(e.target.value);
  };

  // Format currency value
  const formatCurrency = (value: number | null | undefined): string => {
    if (value === undefined || value === null) return '-';
    if (value === 0) return '-'; // Return dash for zero values
    return `$${value.toFixed(2)}`;
  };

  // Format date as MM/DD/YY
  const formatDate = (date: Date | null | undefined): string => {
    if (!date) return '-';
    try {
      // Check if date is valid
      if (isNaN(date.getTime()) || date.getTime() === 0) {
        return '-';
      }
      return format(date, 'MM/dd/yy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  // Get the background color for the +/- cell
  const getPlusMinusColor = (multiplier: number): string => {
    // Create a gradient from green to light green to light pink to pink
    // with 75% opacity
    switch (multiplier) {
      case 5: return 'bg-green-600/75 text-white dark:bg-green-800/75 dark:text-white';
      case 4: return 'bg-green-500/75 text-white dark:bg-green-700/75 dark:text-white';
      case 3: return 'bg-green-400/75 text-white dark:bg-green-600/75 dark:text-white';
      case 2: return 'bg-green-300/75 dark:bg-green-500/75 dark:text-white';
      case 1: return 'bg-green-200/75 dark:bg-green-400/75';
      case -1: return 'bg-pink-200/75 dark:bg-pink-400/75';
      case -2: return 'bg-pink-300/75 dark:bg-pink-500/75 dark:text-white';
      case -3: return 'bg-pink-400/75 text-white dark:bg-pink-600/75 dark:text-white';
      case -4: return 'bg-pink-500/75 text-white dark:bg-pink-700/75 dark:text-white';
      case -5: return 'bg-pink-600/75 text-white dark:bg-pink-800/75 dark:text-white';
      default: return '';
    }
  };

  // Get the background color for the Profit Per Day cell
  const getProfitPerDayColor = (value: number | null | undefined): string => {
    if (value === undefined || value === null) return '';
    
    // Light green for positive values, light red for $0 and negative values
    return value > 0 
      ? 'bg-green-100 dark:bg-green-900/30' 
      : 'bg-red-100 dark:bg-red-900/30';
  };

  if (!product) return null;

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">GPO Performance</h2>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
          <span className="ml-2">Loading data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">GPO Performance</h2>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-md text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">GPO Performance</h2>
        <p className="text-gray-500 dark:text-gray-400">No GPO performance data available for this product.</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
      <h2 className="text-2xl font-bold mb-6">GPO Performance</h2>
      
      <div className="overflow-x-auto">
        <Table className="border-collapse">
          <TableHeader>
            <TableRow className="py-1">
              <TableHead className="w-1/12">#</TableHead>
              <TableHead className="w-1/12">Gross Profit</TableHead>
              <TableHead className="w-1/12">Max Bid</TableHead>
              <TableHead className="w-1/12">Start Date</TableHead>
              <TableHead className="w-1/12">Days</TableHead>
              <TableHead className="w-1/12">Profit Per Day</TableHead>
              <TableHead className="w-1/12">End Date</TableHead>
              <TableHead className="w-1/12">Season 1 Days</TableHead>
              <TableHead className="w-1/12">Season 2 Days</TableHead>
              <TableHead className="w-1/12">Farming Start</TableHead>
              <TableHead className="w-1/12">Farming End</TableHead>
              <TableHead className="w-1/12">Days Farming</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.index} className={`py-1 ${row.index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-900'}`}>
                <TableCell>{row.index === 0 ? '-' : row.index}</TableCell>
                <TableCell>{row.grossProfit === 0 ? '-' : formatCurrency(row.grossProfit)}</TableCell>
                <TableCell>{row.maxBid === 0 ? '-' : formatCurrency(row.maxBid)}</TableCell>
                <TableCell>{row.startDate.getTime() === 0 ? '-' : formatDate(row.startDate)}</TableCell>
                <TableCell>{row.days === 0 ? '-' : row.days}</TableCell>
                <TableCell className={row.profitPerDay === 0 ? '' : getProfitPerDayColor(row.profitPerDay)}>
                  {row.profitPerDay === 0 ? '-' : formatCurrency(row.profitPerDay)}
                </TableCell>
                <TableCell>{row.endDate.getTime() === 0 ? '-' : formatDate(row.endDate)}</TableCell>
                <TableCell>{row.season1Days || '-'}</TableCell>
                <TableCell>{row.season2Days || '-'}</TableCell>
                <TableCell>{formatDate(row.farmingStartDate)}</TableCell>
                <TableCell>{formatDate(row.farmingEndDate)}</TableCell>
                <TableCell>{row.daysFarming || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Next GPO Possibilities Section */}
      <div className="mt-10">
        <div className="flex items-center mb-4">
          <div className="w-64">
            <Label htmlFor="increment" className="mr-2">Increment</Label>
            <div className="flex items-center">
              <span className="mr-1">$</span>
              <Input
                id="increment"
                type="number"
                step="0.01"
                min="0"
                value={increment}
                onChange={handleIncrementChange}
                className="w-24"
              />
            </div>
          </div>
          <h3 className="text-xl font-bold">Next GPO Possibilities</h3>
        </div>

        <div className="overflow-x-auto">
          <Table className="border-collapse">
            <TableHeader>
              <TableRow className="py-1">
                <TableHead className="w-1/12">+/-</TableHead>
                <TableHead className="w-1/12">New GPO</TableHead>
                <TableHead className="w-1/12">New Max Bid</TableHead>
                <TableHead className="w-1/12">ASIN</TableHead>
                <TableHead className="w-1/12">Date</TableHead>
                <TableHead className="w-1/12">Your Name</TableHead>
                {/* Removed Data Element Header */}
                <TableHead className="w-1/12">New Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {nextGpoPossibilities.map((row, index) => (
                <TableRow key={index} className="py-1">
                  <TableCell className={getPlusMinusColor(row.plusMinus)}>
                    {formatCurrency(row.plusMinusValue)}
                  </TableCell>
                  <TableCell>
                    {typeof row.newGpo === 'number' ? formatCurrency(row.newGpo) : row.newGpo}
                  </TableCell>
                  <TableCell>
                    {typeof row.newMaxBid === 'number' ? formatCurrency(row.newMaxBid) : row.newMaxBid}
                  </TableCell>
                  <TableCell>{row.asin}</TableCell>
                  <TableCell>{formatDate(row.date)}</TableCell>
                  <TableCell>{row.yourName}</TableCell>
                  {/* Removed Data Element Cell */}
                  <TableCell>
                    {typeof row.newValue === 'number' ? formatCurrency(row.newValue) : row.newValue}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default GpoPerformanceDashboard;